var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDismissGuard } from '../../customHooks/useDismissGuard';
import { useLockBodyScroll } from '../../customHooks/useLockBodyScroll';
import { usePreventTouchScroll } from '../../customHooks/usePreventTouchScroll';
import { isIos } from '../../../helpers/deviceCheck/isIos';
import TabTrap from '../../../helpers/TabTrap';
import { FooterWrapper, ModalWrapper, Overlay, TransparentScreenCover } from './subcomponents';
import { shouldDisableDismissGuard } from './helpers';
const SimpleModalPortal = (_a) => {
    var { id, children, header, className, footerButtons, isOpen, escapeModalHandler, containerId, onCloseTransitionEnd = () => { }, preventScroll = false, initialFocusTarget } = _a, props = __rest(_a, ["id", "children", "header", "className", "footerButtons", "isOpen", "escapeModalHandler", "containerId", "onCloseTransitionEnd", "preventScroll", "initialFocusTarget"]);
    const TRANSITION_DURATION = 250;
    const [postAnimationState, setPostAnimationState] = useState(false);
    const parentElement = document.getElementById(containerId) || document.body;
    const modalRef = useRef(null);
    const screenCoverRef = useRef(null);
    /** IOS is finnicky, so this is a safeguard to prevent scrolling via preventDefault { passive: false } */
    usePreventTouchScroll({
        ref: screenCoverRef,
        enabled: isIos() && preventScroll
    });
    useLockBodyScroll({ enabled: preventScroll, active: isOpen });
    const { onPointerDown, onPointerUp } = useDismissGuard({
        refs: modalRef,
        onDismiss: escapeModalHandler,
        disableGuard: shouldDisableDismissGuard()
    });
    useEffect(() => {
        const modal = document.getElementById(id);
        if (isOpen && !postAnimationState) {
            modal.style.transform = 'translate(-50%, -25%)';
            modal.style.opacity = '1';
            setPostAnimationState(true);
            return () => { };
        }
        if (!isOpen && postAnimationState) {
            if (modal) {
                modal.style.transform = 'translate(-50%, -60%)';
                modal.style.opacity = '0';
            }
            const timerId = setTimeout(() => {
                setPostAnimationState(false);
                onCloseTransitionEnd();
            }, TRANSITION_DURATION);
            return () => {
                clearTimeout(timerId);
            };
        }
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    return createPortal(_jsxs(_Fragment, { children: [_jsx(Overlay, Object.assign({ isOpen: postAnimationState, onPointerDown: onPointerDown, onPointerUp: onPointerUp, "data-testid": `simple-modal-overlay-${id}` }, { children: (isOpen || postAnimationState) && (_jsx(TabTrap, Object.assign({ initialFocusTarget: initialFocusTarget }, { children: _jsxs(ModalWrapper, Object.assign({}, props, { id: id, escapeModalHandler: escapeModalHandler, "aria-hidden": !isOpen, className: className, transitionDuration: TRANSITION_DURATION, modalRef: modalRef }, { children: [!!header && header, children, footerButtons && (_jsx(FooterWrapper, Object.assign({ "$shouldAlignCenter": footerButtons.length === 1 }, { children: footerButtons })))] })) }))) })), preventScroll && isOpen && (_jsx(TransparentScreenCover, { ref: screenCoverRef, className: "transparentoverlay", onClick: () => {
                    if (modalRef.current) {
                        escapeModalHandler();
                    }
                } }))] }), parentElement);
};
export default SimpleModalPortal;
//# sourceMappingURL=SimpleModalPortal.js.map